import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiService } from "../services/api";
import { Dataset, Project } from "../types/api";
import { DatasetDetailPanel } from "./DatasetDetailPanel";
import { isWorkbookDataset, saveDatasetBlob } from "../utils/datasetDownload";

interface DatasetBrowserProps {
  navigateToRequestRoute?: string;
}

type FilterKey = "all" | "available" | "request";

const dsKey = (ds: Dataset) => `${ds.data_service_id ?? 0}-${ds.id}`;

const DownloadIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M4 16v1a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-1M7 10l5 5 5-5M12 15V3" />
  </svg>
);

const LockIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

export const DatasetBrowser: React.FC<DatasetBrowserProps> = ({
  navigateToRequestRoute,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [downloading, setDownloading] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"name" | "date">("date");
  // `${data_service_id}-${id}` -> resolved project_id for download (auto-selected, not user-chosen)
  const [downloadProjectMap, setDownloadProjectMap] = useState<
    Record<string, number>
  >({});

  const initialFilter: FilterKey =
    location.state?.filterType === "downloadable-only"
      ? "available"
      : location.state?.filterType === "request-only"
        ? "request"
        : "all";
  const [activeFilter, setActiveFilter] = useState<FilterKey>(initialFilter);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [resp, projs] = await Promise.all([
        apiService.getDatasets("browsable"),
        apiService.getProjects(),
      ]);
      const allDatasets = resp.datasets || [];
      const allProjects = projs || [];
      setDatasets(allDatasets);
      setProjects(allProjects);

      // Auto-resolve the correct project for each downloadable dataset.
      // Security: a dataset must always be downloaded in its canonical project context.
      // Priority: uploaded_for_project_id > metadata_project_ids[0] > first user project (org-wide only).
      if (allProjects.length > 0) {
        const projectSet = new Set(allProjects.map((p) => p.id));
        const initial: Record<string, number> = {};
        for (const ds of allDatasets) {
          if (!ds.can_download) continue;
          const key = dsKey(ds);
          if (
            ds.uploaded_for_project_id &&
            projectSet.has(ds.uploaded_for_project_id)
          ) {
            initial[key] = ds.uploaded_for_project_id;
          } else if (ds.metadata_project_ids?.length) {
            const match = ds.metadata_project_ids.find((id) =>
              projectSet.has(id),
            );
            if (match) initial[key] = match;
          } else {
            initial[key] = allProjects[0].id;
          }
        }
        setDownloadProjectMap(initial);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load datasets");
    } finally {
      setLoading(false);
    }
  };

  const resolveProjectTitle = (ds: Dataset): string => {
    if (ds.uploaded_for_project_id) {
      const p = projects.find((p) => p.id === ds.uploaded_for_project_id);
      if (p) return p.title;
    }
    if (ds.metadata_project_ids?.length) {
      for (const id of ds.metadata_project_ids) {
        const p = projects.find((p) => p.id === id);
        if (p) return p.title;
      }
    }
    return "Organization-wide";
  };

  const matchesSearch = (ds: Dataset) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      ds.display_name.toLowerCase().includes(q) ||
      (ds.description?.toLowerCase().includes(q) ?? false) ||
      (ds.uploader_name?.toLowerCase().includes(q) ?? false)
    );
  };

  const sorted = useMemo(() => {
    const result = [...datasets];
    if (sortBy === "name") {
      result.sort((a, b) => a.display_name.localeCompare(b.display_name));
    } else {
      result.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    }
    return result;
  }, [datasets, sortBy]);

  // Pill counts reflect the full (unsearched) set — search narrows what's
  // displayed within the active pill, it doesn't change the pill totals.
  const allCount = sorted.length;
  const availableCount = useMemo(
    () => sorted.filter((d) => d.can_download).length,
    [sorted],
  );
  const requestCount = allCount - availableCount;

  const searched = useMemo(
    () => sorted.filter(matchesSearch),
    [sorted, searchQuery],
  );
  const displayAvailable = useMemo(
    () => searched.filter((d) => d.can_download),
    [searched],
  );
  const displayRequestOnly = useMemo(
    () => searched.filter((d) => !d.can_download),
    [searched],
  );

  const showAvailableGroup =
    activeFilter === "all" || activeFilter === "available";
  const showRequestGroup = activeFilter === "all" || activeFilter === "request";
  const showGroupLabels = activeFilter === "all";


  const visible = useMemo(
    () => [
      ...(showAvailableGroup ? displayAvailable : []),
      ...(showRequestGroup ? displayRequestOnly : []),
    ],
    [showAvailableGroup, showRequestGroup, displayAvailable, displayRequestOnly],
  );

  const selected = visible.find((d) => dsKey(d) === selectedKey) || null;


  useEffect(() => {
    if (visible.length === 0) {
      if (selectedKey !== null) setSelectedKey(null);
      return;
    }
    if (!visible.some((d) => dsKey(d) === selectedKey)) {
      setSelectedKey(dsKey(visible[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleDownload = async (
    dataset: Dataset,
    format: "native" | "csv" = "native",
  ) => {
    const projectId = downloadProjectMap[dsKey(dataset)];
    if (!projectId) return;
    const key = dsKey(dataset);
    setDownloading(key);
    try {
      const blob = await apiService.downloadDatasetPseudonymized(
        dataset.id,
        projectId,
        dataset.data_service_id,
        format,
      );
      saveDatasetBlob(blob, dataset, format);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(null);
    }
  };

  const handleRequestAccess = (dataset: Dataset) => {
    navigate(navigateToRequestRoute || "/requests/new", {
      state: { preselectedDataset: dataset },
    });
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-gray-500">
        <div className="animate-spin inline-block w-8 h-8 border-4 border-sand-300 border-t-ink rounded-full mb-4"></div>
        <p>Loading datasets...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={loadData}
            className="ml-3 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <style>{`
        .bd-title {
          margin: 0 0 4px;
          font-size: 24px;
          color: var(--text-primary);
        }
        .bd-subtitle {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 0 0 20px;
        }
        .bd-search {
          width: 100%;
          height: 36px;
          padding: 0 12px;
          border: 0.5px solid var(--border-strong);
          border-radius: var(--radius);
          font-size: 14px;
          font-family: inherit;
          background: var(--surface-2);
          color: var(--text-primary);
          margin-bottom: 14px;
          box-sizing: border-box;
        }
        .bd-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .bd-filters {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .bd-filter-pill {
          padding: 7px 14px;
          border-radius: 999px;
          font-size: 13px;
          font-weight: 600;
          font-family: inherit;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
          background: var(--surface-1);
          color: var(--text-secondary);
        }
        .bd-filter-pill:hover {
          background: var(--border);
        }
        .bd-filter-pill.active {
          background: var(--fill-primary);
          color: var(--on-primary);
        }
        .bd-sort {
          padding: 7px 10px;
          border: 0.5px solid var(--border-strong);
          border-radius: var(--radius);
          font-size: 13px;
          font-family: inherit;
          background: var(--surface-2);
          color: var(--text-primary);
        }
        .bd-split {
          display: grid;
          grid-template-columns: 380px 1fr;
          border: 0.5px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          min-height: 480px;
        }
        .bd-list-panel {
          background: var(--surface-1);
          border-right: 0.5px solid var(--border);
          padding: 0.9rem;
          overflow: auto;
        }
        .bd-group-label {
          font-size: 12px;
          font-weight: 500;
          margin: 0 0 6px;
        }
        .bd-group-list {
          border: 0.5px solid var(--border);
          border-radius: var(--radius);
          overflow: hidden;
          margin-bottom: 16px;
          background: var(--surface-2);
        }
        .bd-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 10px;
          cursor: pointer;
          border-left: 2px solid transparent;
        }
        .bd-row:not(:last-child) {
          border-bottom: 0.5px solid var(--border);
        }
        .bd-row:hover {
          background: var(--surface-1);
        }
        .bd-row.selected {
          background: var(--bg-accent-muted);
          border-left-color: var(--border-accent);
        }
        .bd-row-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .bd-row-text {
          flex: 1;
          min-width: 0;
        }
        .bd-row-name {
          font-size: 13px;
          font-weight: 600;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .bd-row.selected .bd-row-name {
          color: var(--text-accent);
        }
        .bd-row-sub {
          font-size: 11px;
          color: var(--text-muted);
          margin: 2px 0 0;
        }
        .bd-detail-pane {
          padding: 1.5rem 2rem;
          overflow: auto;
        }
        .bd-detail-empty {
          margin: 0;
          font-size: 14px;
          color: var(--text-muted);
        }
        .bd-empty {
          padding: 40px;
          text-align: center;
          color: var(--text-muted);
          font-size: 13px;
        }
      `}</style>

      <div className="flex justify-between items-start">
        <div>
          <h1 className="bd-title">Browse Datasets</h1>
          <p className="bd-subtitle">
            Every dataset you can see across your projects, whether or not you
            have access yet
          </p>
        </div>
      </div>

      <input
        type="text"
        placeholder="Search by name, description, or uploader..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="bd-search"
      />

      <div className="bd-controls">
        <div className="bd-filters">
          <button
            className={`bd-filter-pill ${activeFilter === "all" ? "active" : ""}`}
            onClick={() => setActiveFilter("all")}
          >
            All ({allCount})
          </button>
          <button
            className={`bd-filter-pill ${activeFilter === "available" ? "active" : ""}`}
            onClick={() => setActiveFilter("available")}
          >
            <DownloadIcon />
            Available for download ({availableCount})
          </button>
          <button
            className={`bd-filter-pill ${activeFilter === "request" ? "active" : ""}`}
            onClick={() => setActiveFilter("request")}
          >
            <LockIcon />
            Needs a request ({requestCount})
          </button>
        </div>
        <select
          className="bd-sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as "name" | "date")}
        >
          <option value="date">Recently added</option>
          <option value="name">Name (A-Z)</option>
        </select>
      </div>

      {allCount === 0 ? (
        <div className="bd-empty">No datasets available to your projects</div>
      ) : (
        <div className="bd-split">
          <div className="bd-list-panel">
            {displayAvailable.length === 0 &&
            displayRequestOnly.length === 0 ? (
              <p style={{ fontSize: "13px", color: "var(--text-muted)" }}>
                No datasets match your search
              </p>
            ) : (
              <>
                {showAvailableGroup && displayAvailable.length > 0 && (
                  <div>
                    {showGroupLabels && (
                      <p
                        className="bd-group-label"
                        style={{ color: "var(--text-success)" }}
                      >
                        Available for download ({displayAvailable.length})
                      </p>
                    )}
                    <div className="bd-group-list">
                      {displayAvailable.map((ds) => (
                        <div
                          key={dsKey(ds)}
                          className={`bd-row ${selectedKey === dsKey(ds) ? "selected" : ""}`}
                          onClick={() => setSelectedKey(dsKey(ds))}
                        >
                          <span
                            className="bd-row-dot"
                            style={{ background: "var(--text-success)" }}
                          />
                          <div className="bd-row-text">
                            <p className="bd-row-name">{ds.display_name}</p>
                            <p className="bd-row-sub">
                              {resolveProjectTitle(ds)} ·{" "}
                              {ds.row_count?.toLocaleString() ?? "N/A"} rows
                              {ds.column_metadata &&
                                ` · ${ds.column_metadata.length} columns`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {showRequestGroup && displayRequestOnly.length > 0 && (
                  <div>
                    {showGroupLabels && (
                      <p
                        className="bd-group-label"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        Needs a request ({displayRequestOnly.length})
                      </p>
                    )}
                    <div className="bd-group-list">
                      {displayRequestOnly.map((ds) => (
                        <div
                          key={dsKey(ds)}
                          className={`bd-row ${selectedKey === dsKey(ds) ? "selected" : ""}`}
                          onClick={() => setSelectedKey(dsKey(ds))}
                        >
                          <span
                            className="bd-row-dot"
                            style={{ background: "var(--text-muted)" }}
                          />
                          <div className="bd-row-text">
                            <p className="bd-row-name">{ds.display_name}</p>
                            <p className="bd-row-sub">
                              {resolveProjectTitle(ds)} ·{" "}
                              {ds.row_count?.toLocaleString() ?? "N/A"} rows
                              {ds.column_metadata &&
                                ` · ${ds.column_metadata.length} columns`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="bd-detail-pane">
            {!selected && (
              <p className="bd-detail-empty">
                {searchQuery.trim()
                  ? "No datasets match your search."
                  : activeFilter === "available"
                    ? "No datasets are available for direct download."
                    : activeFilter === "request"
                      ? "No datasets need a request — you already have access to everything you can see."
                      : "No datasets to show."}
              </p>
            )}
            {selected && (
              <DatasetDetailPanel
                dataset={selected}
                crumb={resolveProjectTitle(selected)}
                actionButton={
                  selected.can_download ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <button
                        className="dd-action-btn"
                        onClick={() => handleDownload(selected)}
                        disabled={
                          downloading === dsKey(selected) ||
                          !downloadProjectMap[dsKey(selected)]
                        }
                      >
                        <DownloadIcon size={16} />
                        {downloading === dsKey(selected)
                          ? "Downloading…"
                          : isWorkbookDataset(selected)
                            ? "Download workbook"
                            : "Download"}
                      </button>
                      {isWorkbookDataset(selected) && (
                        <button
                          className="dd-action-btn"
                          style={{ opacity: 0.85 }}
                          onClick={() => handleDownload(selected, "csv")}
                          disabled={
                            downloading === dsKey(selected) ||
                            !downloadProjectMap[dsKey(selected)]
                          }
                          title="Flattened first sheet, supports column selection"
                        >
                          Download as CSV
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      className="dd-action-btn"
                      onClick={() => handleRequestAccess(selected)}
                    >
                      Request access
                    </button>
                  )
                }
              >
                {selected.description && (
                  <p
                    style={{
                      fontSize: "14px",
                      color: "var(--text-secondary)",
                      margin: "0 0 20px",
                      maxWidth: "560px",
                    }}
                  >
                    {selected.description}
                  </p>
                )}
              </DatasetDetailPanel>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
